// One chart of the System pages: NextAdmin `charts/line-charts` inside a `ChartCard` with its stats header
// (demo `/analytics`). The data is a `MetricSeriesSet` from the server; the card only lays it out.
import type React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  ChartCard,
  type ChartStat,
  type LegendItem,
  LineChart,
  SERIES_TONES,
} from '../../../components/charts/index.js';
import { Callout, Skeleton } from '../../../components/ui/index.js';
import type { MetricSeriesSet } from '../../../generated/api/types.gen.js';
import { ApiError } from '../../../services/api/errors.js';
import { formatAxisMetric, formatChartTime, formatMetric, type MetricRange } from './metric-format.js';

interface SeriesChartCardProps {
  title: string;
  description?: string;
  range: MetricRange;
  data: MetricSeriesSet | undefined;
  loading: boolean;
  error: unknown;
  /** Fill under the lines (levels such as memory or size); lines otherwise. */
  area?: boolean;
}

type Row = Record<string, number | null>;

/** Rows keyed by time, one column per series (`s0`, `s1`…: Recharts reads dotted keys as paths). */
export function toRows(set: MetricSeriesSet): Row[] {
  const byTime = new Map<number, Row>();
  set.series.forEach((series, index) => {
    for (const point of series.points) {
      const row = byTime.get(point.t) ?? { t: point.t };
      row[`s${index}`] = point.v;
      byTime.set(point.t, row);
    }
  });
  return [...byTime.values()].sort((a, b) => (a.t ?? 0) - (b.t ?? 0));
}

function headline(set: MetricSeriesSet): ChartStat[] {
  const first = set.series[0];
  if (!first || first.points.length === 0) return [];
  const last = first.points[first.points.length - 1];
  const peakSeries = set.series.find((s) => s.stat === 'max' && s.key.startsWith(first.key)) ?? first;
  const peak = Math.max(...peakSeries.points.map((p) => p.v));
  return [
    { id: 'now', label: 'Now', value: formatMetric(set.unit, last?.v), tone: 'accent' },
    { id: 'peak', label: 'Peak in range', value: formatMetric(set.unit, peak), tone: 'warning' },
  ];
}

export function SeriesChartCard({ title, description, range, data, loading, error, area }: SeriesChartCardProps) {
  const series = (data?.series ?? []).map((s, index) => ({
    key: `s${index}`,
    label: s.label,
    tone: SERIES_TONES[index % SERIES_TONES.length] ?? 'accent',
  }));
  const legend: LegendItem[] = series.length > 1 ? series.map((s) => ({ label: s.label, tone: s.tone })) : [];
  const unit = data?.unit ?? 'count';

  let body: React.ReactNode;
  if (loading) {
    body = <Skeleton height={220} />;
  } else if (error) {
    body = (
      <Callout tone="danger" icon={AlertTriangle} title="This chart could not be loaded">
        {error instanceof ApiError ? error.message : 'The request failed.'}
      </Callout>
    );
  } else if (!data || series.length === 0 || data.series.every((s) => s.points.length === 0)) {
    body = (
      <p className="flex h-[220px] items-center justify-center text-center text-sm text-muted">
        No data in this range yet.
      </p>
    );
  } else {
    body = (
      <LineChart
        data={toRows(data)}
        xKey="t"
        series={series}
        title={title}
        summary={description}
        height={220}
        area={area}
        formatValue={(value) => formatMetric(unit, value)}
        formatAxis={(value) => formatAxisMetric(unit, value)}
        formatX={(value) => formatChartTime(Number(value), range)}
      />
    );
  }

  return (
    <ChartCard title={title} description={description} legend={legend} stats={data ? headline(data) : undefined}>
      {body}
    </ChartCard>
  );
}
